import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Typography, Button, Spin, Alert, Empty, Tag } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';

import { professorAvaliacoesService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';
import AlunoAvaliacaoProfessorCard from './AlunoAvaliacaoProfessorCard';
import '../../styles/PortalProfessor.css';

const { Title, Text } = Typography;

const MENSAGEM_SEM_VINCULO =
  'Seu usuário ainda não está vinculado a um professor/equipe. Procure a administração.';
const MENSAGEM_NAO_ENCONTRADA = 'Esta turma não está disponível.';

// Contrato de GET /api/professor/avaliacoes/turmas/{id}/alunos: devolve
// SOMENTE identidade + status de cada aluno -- não inclui nome_turma/
// nome_periodo/status_periodo. Por isso o cabeçalho de contexto desta
// página usa o `resumo` recebido via router state (navegação a partir de
// PortalProfessorAvaliacoes.js, que já tem esses campos do GET
// /api/professor/avaliacoes). Quem chega aqui por URL direta (sem state,
// ex.: recarregar a página ou favorito) não vê o cabeçalho de contexto --
// a lista de alunos continua funcionando normalmente; não é chamada uma
// segunda rota só para recuperar esses três campos textuais.
const PortalProfessorAvaliacaoTurma = () => {
  const { avaliacaoTurmaId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const resumo = location.state?.resumo;

  const [alunos, setAlunos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null); // { status, message } | null

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const response = await professorAvaliacoesService.listarAlunos(avaliacaoTurmaId);
      setAlunos(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setErro({ status, message: MENSAGEM_SEM_VINCULO });
      } else if (status === 404) {
        setErro({ status, message: MENSAGEM_NAO_ENCONTRADA });
      } else {
        const { message: mensagem } = buildApiErrorMessage(
          err,
          'Não foi possível carregar os alunos desta turma. Tente novamente.'
        );
        setErro({ status, message: mensagem });
      }
      setAlunos([]);
    } finally {
      setLoading(false);
    }
  }, [avaliacaoTurmaId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Ordem devolvida pelo backend é mantida -- nunca reordenada aqui. A
  // lista de IDs, na mesma ordem, viaja no state para permitir "Próximo
  // aluno" na tela de detalhe sem precisar de estado global.
  const idsAlunosNaOrdem = alunos.map((a) => a.id_avaliacao_aluno);

  const abrirAluno = (aluno) => {
    navigate(`/professor/avaliacoes/alunos/${aluno.id_avaliacao_aluno}`, {
      state: { avaliacaoTurmaId, idsAlunosNaOrdem, resumo },
    });
  };

  const voltar = () => navigate('/professor/avaliacoes');

  const encerrado = resumo?.status_periodo === 'ENCERRADO';

  return (
    <div className="portal-professor">
      <Button
        icon={<ArrowLeftOutlined />}
        type="text"
        onClick={voltar}
        aria-label="Voltar para avaliações"
        style={{ marginBottom: 12 }}
      >
        Voltar
      </Button>

      {resumo && (
        <div className="portal-professor__header">
          <div>
            <Title level={3} className="portal-professor__title">
              {resumo.nome_turma}
            </Title>
            <Text type="secondary" className="portal-professor__subtitle">
              {resumo.nome_periodo}
            </Text>
            {encerrado && (
              <div style={{ marginTop: 4 }}>
                <Tag color="default">Encerrada — somente consulta</Tag>
              </div>
            )}
          </div>
        </div>
      )}

      {loading && (
        <div className="portal-professor__state" role="status" aria-live="polite">
          <Spin size="large" />
          <Text type="secondary">Carregando alunos...</Text>
        </div>
      )}

      {!loading && erro && (
        <div className="portal-professor__state">
          <Alert
            type={erro.status === 403 || erro.status === 404 ? 'warning' : 'error'}
            showIcon
            message="Não foi possível abrir a turma"
            description={erro.message}
            style={{ maxWidth: 480, textAlign: 'left' }}
          />
          {erro.status !== 403 && erro.status !== 404 && (
            <Button icon={<ReloadOutlined />} onClick={carregar}>
              Tentar novamente
            </Button>
          )}
        </div>
      )}

      {!loading && !erro && alunos.length === 0 && (
        <div className="portal-professor__state">
          <Empty description="Nenhum aluno disponível para avaliação nesta turma." />
        </div>
      )}

      {!loading && !erro && alunos.length > 0 && (
        <div className="portal-professor__list">
          {alunos.map((aluno) => (
            <AlunoAvaliacaoProfessorCard key={aluno.id_avaliacao_aluno} aluno={aluno} onAbrir={abrirAluno} />
          ))}
        </div>
      )}
    </div>
  );
};

export default PortalProfessorAvaliacaoTurma;
